import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

export interface DuplicateContactDraft {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
  mobile?: string;
  type?: string;
}

export interface DuplicateContactAssign {
  contactId: string;
  addAsAlternate: boolean;
  /** Label for the new alternate entry; null when the agent leaves it empty. */
  label: string;
}

@Component({
  selector: 'app-possible-duplicate-contact',
  templateUrl: './possible-duplicate-contact.component.html',
  styleUrls: ['./possible-duplicate-contact.component.scss'],
})
export class PossibleDuplicateContactComponent implements OnChanges {
  @Input() visible = false;
  @Input() newContact: DuplicateContactDraft = {};
  @Input() contacts: any[] = [];
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() assign = new EventEmitter<DuplicateContactAssign>();
  @Output() createNew = new EventEmitter<void>();

  activeIndex = 0;
  isSamePerson = true;
  selectedLabel: string = null;

  // Same list as the contact's Alternative Information rows, so the saved label reads the same there.
  readonly labelOptions: string[] = ['Personal', 'Work', 'Home', 'Spouse', 'Partner', 'Other'];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contacts'] || (changes['visible'] && this.visible)) {
      this.activeIndex = 0;
      this.isSamePerson = true;
      this.selectedLabel = null;
    }
  }

  addCustomLabel = (label: string) => label;

  get existing(): any {
    return this.contacts?.[this.activeIndex] ?? null;
  }

  get newFirstName(): string {
    return this.newContact?.firstName || this.newContact?.fullName?.split(' ')[0] || '';
  }

  get newLastName(): string {
    return this.newContact?.lastName ?? '';
  }

  get existingFirstName(): string {
    return this.existing?.first_name || this.existing?.full_name?.split(' ')[0] || '';
  }

  get existingLastName(): string {
    return this.existing?.last_name ?? '';
  }

  get isSameName(): boolean {
    const newName = normalizeText(`${this.newFirstName} ${this.newLastName}`);
    const existingName = normalizeText(`${this.existingFirstName} ${this.existingLastName}`);
    return newName !== '' && newName === existingName;
  }

  /**
   * True when the lead is a different person on a shared email/mobile (Bob vs Barbara Smith) and
   * isn't already saved as one of the contact's alternate contacts.
   */
  get isNewName(): boolean {
    const name = normalizeText(`${this.newFirstName} ${this.newLastName}`);
    if (!name || !this.existing || this.isSameName) {
      return false;
    }
    return !(this.existing.alternate_contacts ?? []).some(item => normalizeText(item?.name) === name);
  }

  /** True when the lead's email is not already on the contact (primary or alternate). */
  get isNewEmail(): boolean {
    const email = normalizeText(this.newContact?.email);
    if (!email || !this.existing) {
      return false;
    }
    const known = [
      this.existing.email,
      ...(this.existing.secondary_emails ?? []).map(item => item?.email),
      ...(this.existing.alternate_contacts ?? []).map(item => item?.email),
    ];
    return !known.some(value => normalizeText(value) === email);
  }

  /** True when the lead's mobile is not already on the contact (primary or alternate). */
  get isNewMobile(): boolean {
    const mobile = normalizePhone(this.newContact?.mobile);
    if (!mobile || !this.existing) {
      return false;
    }
    const known = [
      this.existing.mobile_no,
      ...(this.existing.secondary_mobile ?? []).map(item => item?.mobile_no),
      ...(this.existing.alternate_contacts ?? []).map(item => item?.mobile_no),
    ];
    return !known.some(value => normalizePhone(value) === mobile);
  }

  get isEmailMatch(): boolean {
    return !!this.newContact?.email && !this.isNewEmail;
  }

  get isMobileMatch(): boolean {
    return !!normalizePhone(this.newContact?.mobile) && !this.isNewMobile;
  }

  get newInitials(): string {
    return toInitials(this.newFirstName, this.newLastName);
  }

  get existingInitials(): string {
    return toInitials(this.existingFirstName, this.existingLastName);
  }

  get hasNewDetails(): boolean {
    return this.isNewName || this.isNewEmail || this.isNewMobile;
  }

  /**
   * Link as an alternate whenever the lead is someone other than the contact's own person — even
   * if that person is already in `alternate_contacts` from an earlier lead. The backend only
   * protects leads listed by `lead_id`; a plain link would let the next contact save rename this
   * lead to the contact's name. A lead for the contact's own person (same name) only becomes an
   * alternate when it brings a new email/mobile.
   */
  get linksAsAlternate(): boolean {
    const isOtherPerson = !!normalizeText(`${this.newFirstName} ${this.newLastName}`) && !this.isSameName;
    return isOtherPerson || this.hasNewDetails;
  }

  get matchLabel(): string {
    if (this.isSameName) {
      return 'Same Name';
    }
    if (!this.isNewEmail && this.newContact?.email) {
      return 'Same Email';
    }
    return 'Same Mobile';
  }

  get alternateLabel(): string {
    const parts = [this.isNewName ? 'name' : null, this.isNewEmail ? 'email' : null, this.isNewMobile ? 'mobile' : null].filter(Boolean);
    return parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts.join('');
  }

  get confirmLabel(): string {
    return this.isSamePerson ? 'Link Contact' : 'Create Contact';
  }

  showPrevious(): void {
    this.activeIndex = Math.max(this.activeIndex - 1, 0);
  }

  showNext(): void {
    this.activeIndex = Math.min(this.activeIndex + 1, this.contacts.length - 1);
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  confirm(): void {
    if (this.isSamePerson && this.existing) {
      this.assign.emit({
        contactId: this.existing._id,
        addAsAlternate: this.linksAsAlternate,
        label: this.selectedLabel ?? null,
      });
    } else {
      this.createNew.emit();
    }
    this.close();
  }
}

function toInitials(firstName: string, lastName: string): string {
  return `${firstName?.trim()?.[0] ?? ''}${lastName?.trim()?.[0] ?? ''}`.toUpperCase() || '?';
}

function normalizeText(value: string): string {
  return String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

/** Mirrors the backend's normalizePhone (convert-to-contact) so '+61 4…' and '04…' compare equal. */
function normalizePhone(value: string): string {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!digits) {
    return '';
  }
  if (digits.length > 9 && digits.startsWith('61')) {
    return `0${digits.slice(2)}`;
  }
  if (digits.startsWith('0')) {
    return digits;
  }
  return digits.length === 9 ? `0${digits}` : digits;
}
